import { useEffect } from 'react';
import styled from 'styled-components';
import { LuMerge, LuTrash2, LuX, LuBan, LuUndo2 } from 'react-icons/lu';

/**
 * What can be done to the object a person clicked on the video: remove it, say it is not the subject, merge it with
 * another track that is the same object, or take back an object they added. Sits next to the dot, inside the
 * picture; Escape closes it, Delete removes.
 */

const tk = ({ theme: t }) => t.token;

const Card = styled.div`
	position: absolute;
	z-index: 6;
	min-width: ${p => tk(p).controlHeight * 6}px;
	padding: ${p => tk(p).paddingXS}px;
	border-radius: ${p => tk(p).borderRadiusLG}px;
	background: ${p => tk(p).panelGlassBg};
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
	box-shadow: ${p => tk(p).boxShadowSecondary};
	color: ${p => tk(p).colorText};
	font-size: ${p => tk(p).fontSizeSM}px;
`;

const Head = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${p => tk(p).marginXS}px;
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingXS}px`};
	color: ${p => tk(p).colorTextSecondary};
`;

const Item = styled.button`
	display: flex;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
	width: 100%;
	padding: ${p => `${tk(p).paddingXS}px ${tk(p).paddingXS}px`};
	border: 0;
	border-radius: ${p => tk(p).borderRadius}px;
	background: transparent;
	color: inherit;
	font: inherit;
	text-align: left;
	cursor: pointer;
	&:hover,
	&:focus-visible {
		background: ${p => tk(p).colorFillTertiary};
		outline: none;
	}
	kbd {
		margin-left: auto;
		color: ${p => tk(p).colorTextTertiary};
		font-size: ${p => tk(p).fontSizeXS}px;
	}
`;

const Close = styled.button`
	display: inline-flex;
	padding: ${p => tk(p).paddingXXS}px;
	border: 0;
	background: transparent;
	color: ${p => tk(p).colorTextSecondary};
	cursor: pointer;
`;

const Hint = styled.div`
	padding: ${p => `${tk(p).paddingXXS}px ${tk(p).paddingXS}px`};
	color: ${p => tk(p).colorText};
	line-height: ${p => tk(p).lineHeight};
`;

const WIDTH = 210;
const HEIGHT = 170;

const PickMenu = ({
	pick,
	subject = 'object',
	added = false,
	merging = false,
	onRemove,
	onNot,
	onMerge,
	onUndoAdd,
	onClose,
}) => {
	useEffect(() => {
		const onKey = e => {
			if (e.key === 'Escape') onClose();
			else if ((e.key === 'Delete' || e.key === 'Backspace') && !merging) {
				e.preventDefault();
				if (added) onUndoAdd();
				else onRemove();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [added, merging, onClose, onRemove, onUndoAdd]);
	if (!pick) return null;
	// next to the dot, kept inside the picture
	const style = {
		left: Math.max(8, pick.x + 16 + WIDTH > pick.width ? pick.x - 16 - WIDTH : pick.x + 16),
		top: Math.max(8, Math.min(pick.y - 20, pick.height - HEIGHT - 8)),
	};
	return (
		<Card
			style={style}
			role="dialog"
			aria-label={`Correct this ${subject}`}
			onPointerDown={e => e.stopPropagation()}>
			<Head>
				<span>{merging ? 'Merge' : added ? `Added by you` : `This ${subject}`}</span>
				<Close type="button" onClick={onClose} aria-label="Close">
					<LuX />
				</Close>
			</Head>
			{merging ? (
				<Hint>Click the other dot that is the same {subject}. Escape to stop.</Hint>
			) : added ? (
				<Item type="button" onClick={onUndoAdd} autoFocus>
					<LuUndo2 /> Take it back <kbd>Del</kbd>
				</Item>
			) : (
				<>
					<Item type="button" onClick={onRemove} autoFocus>
						<LuTrash2 /> Remove <kbd>Del</kbd>
					</Item>
					<Item type="button" onClick={onNot}>
						<LuBan /> Not a {subject}
					</Item>
					<Item type="button" onClick={onMerge}>
						<LuMerge /> Same as another…
					</Item>
				</>
			)}
		</Card>
	);
};

export default PickMenu;
